<!-- Takes an asset and shows the asset name -->
<template lang="pug">
p(v-if="error") {{ $t("error") }}
p(v-else-if="loading", v-loading="true") 
p(v-else-if="props.id === undefined && props.twin === undefined") -
div(v-else-if="loaded")
  router-link.asset-link(
    v-if="props.link !== false && linkName !== null",
    :to="{ name: linkName, params: { template: assetTemplate, id: id } }"
  ) {{ assetName }}
  .asset-link(v-else) {{ assetName }}
  span.ml-1.text-red-500(v-if="templateMissing") ({{ $t("deleted") }})
  .twin-field-properties.flex(
    v-if="showTextProperties && textProperties.length"
  )
    .ml-2(v-for="property in textProperties", :key="property.uuid")
      TwinPropertyView(:prop="property")
p(v-else) -
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from "vue";
import { useTwinStore } from "../../../stores/twinManagement";
import { useAppStore } from "../../../stores/apps";
import { useTwinCacheStore } from "@shared/stores/twinCache";
import TwinPropertyView from "./TwinPropertyView.vue";
import type { TwinProperty } from "@shared/services/twinManagement";
import type { TwinManagementTypes } from "@inctec/smx-ui-client";

type SearchTwinProperty = NonNullable<
  NonNullable<
    TwinManagementTypes.SearchTwinsResponse["result"]
  >[number]["properties"]
>[number];

const props = withDefaults(
  defineProps<{
    id?: string;
    twin?: {
      name: string;
      uuid: string;
      templateId?: string;
      templateUuid?: string | null;
      properties?: Array<TwinProperty | SearchTwinProperty>;
    };
    showTextProperties?: boolean;
    link?: boolean;
  }>(),
  {
    showTextProperties: true,
    link: true,
  },
);

onMounted(() => {
  if (props.id) {
    void loadSelectedTwin();
  } else if (props.twin) {
    void twinStore.getTwinTemplates();
  } else {
    console.warn("Twin Field: Either id or twin has to be set");
  }
});

const twinLoadState = computed(() => {
  return props.id ? twinCacheStore.twinBaseCache.get(props.id) : undefined;
});

const loading = computed(() => twinLoadState.value?.loading);
const loaded = computed(() => {
  return props.twin ? true : twinLoadState.value?.loaded;
});
const error = computed(() => twinLoadState.value?.error);

const assetTemplate = computed(() => {
  if (props.twin) {
    return props.twin.templateId
      ? props.twin.templateId
      : props.twin.templateUuid;
  }
  return twinLoadState.value?.result?.templateUuid;
});
const assetName = computed(() =>
  props.twin ? props.twin.name : twinLoadState.value?.result?.name,
);
const textProperties = computed(() =>
  (props.twin?.properties ?? [])
    .filter(
      (property) =>
        property.type === "text" &&
        property.propertyValue?.value !== undefined &&
        property.propertyValue.value !== "",
    )
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .slice(0, 2),
);

const appStore = useAppStore();
const twinStore = useTwinStore();

const template = computed(() =>
  twinStore.templatesLoadingState.result?.find(
    (item) => item.uuid === assetTemplate.value,
  ),
);
const templateMissing = computed(
  () =>
    assetTemplate.value !== undefined &&
    assetTemplate.value !== null &&
    twinStore.templatesLoadingState.loaded &&
    !template.value,
);

const linkName = computed(() => {
  if (template.value) {
    return appStore.getLink(
      `twin-edit.${template.value.type}.${assetTemplate.value}.${id.value}`,
    )?.routeName;
  }
  return null;
});

const id = computed(() => {
  return props.twin ? props.twin.uuid : props.id;
});

const twinCacheStore = useTwinCacheStore();

async function loadSelectedTwin() {
  if (props.id) {
    await twinCacheStore.loadTwinBase(props.id);
  }
}

watch(
  () => props.id,
  (id) => {
    if (id) {
      void twinCacheStore.loadTwinBase(id);
    }
  },
);
</script>

<style>
.asset-link {
  color: var(--color-orange-dark);
}
</style>
